import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Plus, Copy, Users, Building2, Shield, User, RotateCw, KeyRound, ChevronDown, ChevronUp, Zap, Trophy, Award, Flame, Clock, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  org_admin: "Org Admin",
  venue_manager: "Venue Manager",
  staff: "Staff",
  admin: "Admin",
};

export function VenueManagement() {
  const [org, setOrg] = useState(null);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newVenueName, setNewVenueName] = useState("");
  const [savingUserId, setSavingUserId] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [orgRes, usersRes] = await Promise.all([
        axios.get(`${API}/org/my-org`),
        axios.get(`${API}/org/staff`),
      ]);
      setOrg(orgRes.data.org);
      setVenues(orgRes.data.venues || []);
      setUsers(usersRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const addVenue = async () => {
    if (!newVenueName.trim()) return;
    try {
      await axios.post(`${API}/org/venues`, { name: newVenueName.trim() });
      setShowAdd(false);
      setNewVenueName("");
      fetchData();
      toast.success("Venue added!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyCode = (code, venueName) => {
    navigator.clipboard.writeText(code).then(() => toast.success(`${venueName} code copied!`));
  };

  const rotateCode = async (venueId, venueName) => {
    if (!window.confirm(`Generate a new code for ${venueName}? The old code will stop working immediately.`)) return;
    try {
      const res = await axios.post(`${API}/org/venues/${venueId}/rotate-code`);
      setVenues(prev => prev.map(v => v.id === venueId ? { ...v, join_code: res.data.join_code } : v));
      toast.success(`New code: ${res.data.join_code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rotate code");
    }
  };

  const staffPerVenue = (venueId) => users.filter(u => u.venue_id === venueId).length;

  const patchStaff = async (userId, patch, successMsg) => {
    setSavingUserId(userId);
    try {
      const res = await axios.patch(`${API}/org/staff/${userId}`, patch);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...res.data } : u));
      toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingUserId(null);
    }
  };

  const toggleVenueManager = (u) => {
    const nextRole = u.role === "venue_manager" ? "staff" : "venue_manager";
    const msg = nextRole === "venue_manager" ? `${u.name} promoted to Venue Manager` : `${u.name} is now Staff`;
    patchStaff(u.user_id, { role: nextRole }, msg);
  };

  const moveToVenue = (u, venueId) => {
    if (venueId === u.venue_id) return;
    const vName = venues.find(v => v.id === venueId)?.name || "venue";
    patchStaff(u.user_id, { venue_id: venueId }, `${u.name} moved to ${vName}`);
  };

  const togglePerformance = async (u) => {
    if (expandedUserId === u.user_id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(u.user_id);
    if (summaries[u.user_id]) return;
    setLoadingSummary(u.user_id);
    try {
      const res = await axios.get(`${API}/admin/staff/${u.user_id}/summary`);
      setSummaries(prev => ({ ...prev, [u.user_id]: res.data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load performance");
      setExpandedUserId(null);
    } finally {
      setLoadingSummary(null);
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return "never";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "recently";
    const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.round(days / 30)}mo ago`;
  };

  if (loading) return null;
  if (!org) return null;

  // Exclude org admins from the editable staff list (they can't be demoted here)
  const editableStaff = users.filter(u => u.role !== "org_admin" && u.role !== "admin");
  const orgAdmins = users.filter(u => u.role === "org_admin" || u.role === "admin");
  // Single-venue simplification: hide the venues grid + Add-Venue button when
  // the org has one venue. Test-period orgs only need one destination for
  // signups; the multi-venue UI reappears automatically if a second venue is
  // created later.
  const singleVenueMode = venues.length <= 1;

  return (
    <div data-testid="venue-management" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Building2 size={20} className="text-[hsl(var(--primary))]" />
            {org.name}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{venues.length} venue{venues.length === 1 ? '' : 's'} · {users.length} total staff</p>
        </div>
        {!singleVenueMode && (
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[hsl(var(--primary))] text-black" data-testid="add-venue-btn">
            <Plus size={14} className="mr-1" /> Add Venue
          </Button>
        )}
      </div>

      {/* Venues grid — hidden when there's only one venue (test-period simplification) */}
      {!singleVenueMode && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {venues.map(v => (
          <Card key={v.id} className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid={`venue-card-${v.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-sm flex items-center gap-2">
                    <MapPin size={14} className="text-[hsl(var(--primary))]" />
                    {v.name}
                  </h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1">
                    <Users size={12} /> {staffPerVenue(v.id)} staff
                  </p>
                </div>
              </div>
              <div className="space-y-1.5" data-testid={`venue-invite-${v.id}`}>
                <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                  <KeyRound size={10} /> Invite Code
                </p>
                <div className="flex items-center gap-2 p-2 rounded-md bg-[hsl(var(--secondary))]">
                  <code className="text-sm font-mono font-bold tracking-wider text-[hsl(var(--primary))] flex-1 truncate" data-testid={`venue-code-${v.id}`}>{v.join_code}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyCode(v.join_code, v.name)} className="h-7 w-7 p-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`copy-code-${v.id}`} aria-label="Copy code">
                    <Copy size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => rotateCode(v.id, v.name)} className="h-7 w-7 p-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`rotate-code-${v.id}`} aria-label="Generate new code" title="Generate a new code (old one stops working)">
                    <RotateCw size={13} />
                  </Button>
                </div>
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] leading-tight">
                  Send this code to staff. They paste it in their profile to join.
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Team management */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="team-management">
        <CardContent className="p-4 space-y-4">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Users size={16} className="text-[hsl(var(--primary))]" />
              Team
            </h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              Promote staff to Venue Manager (can create tests but cannot edit cocktails) or move them between venues.
            </p>
          </div>

          {orgAdmins.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Org Admins</p>
              {orgAdmins.map(u => (
                <div key={u.user_id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(var(--secondary))]/50" data-testid={`org-admin-row-${u.user_id}`}>
                  <Shield size={14} className="text-[hsl(var(--primary))]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.email}</p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">{ROLE_LABELS[u.role] || u.role}</span>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Staff & Venue Managers</p>
            {editableStaff.length === 0 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] italic py-2" data-testid="staff-empty">No staff yet. Share a venue join link to invite your team.</p>
            )}
            {editableStaff.map(u => {
              const isVM = u.role === "venue_manager";
              const isExpanded = expandedUserId === u.user_id;
              const summary = summaries[u.user_id];
              return (
                <div key={u.user_id} className="rounded-md bg-[hsl(var(--secondary))]/40 overflow-hidden" data-testid={`staff-row-${u.user_id}`}>
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                    {isVM ? <Shield size={14} className="text-[hsl(var(--primary))]" /> : <User size={14} className="text-[hsl(var(--muted-foreground))]" />}
                    <button
                      type="button"
                      onClick={() => togglePerformance(u)}
                      className="flex-1 min-w-[140px] text-left hover:text-[hsl(var(--primary))] transition-colors"
                      data-testid={`open-perf-${u.user_id}`}
                    >
                      <p className="text-sm font-medium truncate flex items-center gap-1.5">
                        {u.name}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </p>
                      <p className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.email}</p>
                    </button>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isVM ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))]"}`} data-testid={`role-badge-${u.user_id}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    <Select
                      value={u.venue_id || "_none"}
                      onValueChange={(val) => moveToVenue(u, val === "_none" ? null : val)}
                      disabled={savingUserId === u.user_id}
                    >
                      <SelectTrigger className="h-8 w-[150px] text-xs bg-[hsl(var(--secondary))]" data-testid={`venue-select-${u.user_id}`}>
                        <SelectValue placeholder="No venue" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No venue</SelectItem>
                        {venues.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant={isVM ? "default" : "outline"}
                      className={isVM ? "h-8 text-xs bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90" : "h-8 text-xs"}
                      disabled={savingUserId === u.user_id}
                      onClick={() => toggleVenueManager(u)}
                      data-testid={`toggle-vm-${u.user_id}`}
                    >
                      {isVM ? "Demote" : "Make Venue Manager"}
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-[hsl(var(--border))] px-4 py-4 bg-[hsl(var(--background))]/40 space-y-4" data-testid={`perf-panel-${u.user_id}`}>
                      {loadingSummary === u.user_id && (
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">Loading performance…</p>
                      )}
                      {summary && (
                        <>
                          {/* Header strip */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid={`perf-header-${u.user_id}`}>
                            <div className="rounded-md bg-[hsl(var(--secondary))]/60 p-2.5">
                              <div className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] uppercase tracking-wider text-[10px]"><Trophy size={11} /> Level</div>
                              <p className="mt-1 font-bold text-sm">L{summary.level?.level} · {summary.level?.title}</p>
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{summary.total_xp} XP{summary.level?.next_level ? ` · ${summary.level.next_level.min_xp - summary.total_xp} to L${summary.level.next_level.level}` : ""}</p>
                            </div>
                            <div className="rounded-md bg-[hsl(var(--secondary))]/60 p-2.5">
                              <div className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] uppercase tracking-wider text-[10px]"><BarChart2 size={11} /> Tests</div>
                              <p className="mt-1 font-bold text-sm">{summary.stats.total_tests} taken</p>
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">avg {summary.stats.avg_percentage}% · best {summary.stats.best_percentage}%</p>
                            </div>
                            <div className="rounded-md bg-[hsl(var(--secondary))]/60 p-2.5">
                              <div className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] uppercase tracking-wider text-[10px]"><Flame size={11} /> Streak · Badges</div>
                              <p className="mt-1 font-bold text-sm">{summary.login_streak}d streak</p>
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{summary.challenge_count} practice sessions · {summary.badges_count} badges</p>
                            </div>
                            <div className="rounded-md bg-[hsl(var(--secondary))]/60 p-2.5">
                              <div className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] uppercase tracking-wider text-[10px]"><Clock size={11} /> Last active</div>
                              <p className="mt-1 font-bold text-sm">{formatRelative(summary.last_active)}</p>
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{summary.last_active ? new Date(summary.last_active).toLocaleDateString() : "no activity yet"}</p>
                            </div>
                          </div>

                          {/* Recent activity — two columns */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div data-testid={`perf-tests-${u.user_id}`}>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5 flex items-center gap-1"><Award size={11} /> Last 5 tests</p>
                              {summary.recent_tests.length === 0 ? (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] italic">No tests taken yet.</p>
                              ) : (
                                <div className="space-y-1">
                                  {summary.recent_tests.map((t, i) => (
                                    <div key={t.id || `t-${i}`} className="flex items-center justify-between px-2 py-1.5 rounded bg-[hsl(var(--secondary))]/50 text-xs">
                                      <div className="min-w-0">
                                        <p className="truncate font-medium">{t.test_name}</p>
                                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{new Date(t.completed_at).toLocaleDateString()}{t.perfect_cocktails ? ` · ${t.perfect_cocktails} perfect` : ""}</p>
                                      </div>
                                      <span className={`font-bold ${t.percentage >= 80 ? "text-green-500" : t.percentage >= 60 ? "text-[#C36A2F]" : "text-red-500"}`}>{Math.round(t.percentage)}%</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div data-testid={`perf-challenges-${u.user_id}`}>
                              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5 flex items-center gap-1"><Zap size={11} /> Last 5 practice sessions</p>
                              {summary.recent_challenges.length === 0 ? (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] italic">No practice sessions yet.</p>
                              ) : (
                                <div className="space-y-1">
                                  {summary.recent_challenges.map((c, i) => (
                                    <div key={`c-${i}`} className="flex items-center justify-between px-2 py-1.5 rounded bg-[hsl(var(--secondary))]/50 text-xs">
                                      <div className="min-w-0">
                                        <p className="truncate font-medium">{c.deck_name || c.reason || "Practice"}</p>
                                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}{c.cocktail_name ? ` · ${c.cocktail_name}` : ""}</p>
                                      </div>
                                      <span className="font-bold text-[hsl(var(--primary))]">+{c.xp} XP</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add Venue Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm">
          <DialogHeader><DialogTitle>Add Venue</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={newVenueName} onChange={e => setNewVenueName(e.target.value)} placeholder="e.g. Cardiff Branch" className="bg-[hsl(var(--secondary))]" data-testid="new-venue-name" />
            <Button onClick={addVenue} disabled={!newVenueName.trim()} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="add-venue-submit">
              Add Venue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
